import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useMemo, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { TableSortLabel } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box } from "@mui/system";

interface Props {
  rows: any[];
  total: number;
}

const GapsinBrandData = ({ rows, total }: Props) => {
  const [expanded, setExpanded] = React.useState<string | false>(false);

  const [sortField, setSortField] = useState("brand_id") as any;
  const [sortOrder, setSortOrder] = useState("asc") as any;

  const brandGap = rows?.reduce((sum: any, item: any) => sum + item.gap, 0);

  const percentage = brandGap ? ((brandGap / total) * 100)?.toFixed(2) : 0;

  // Function to handle header clicks
  const handleSortClick = (field: any) => {
    if (sortField === field) {
      // Toggle order if same column
      setSortOrder((prev: any) => (prev === "asc" ? "desc" : "asc"));
    } else {
      // Set new column & reset order to asc
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const filteredAndSortedData = useMemo(() => {
    if (rows?.length) {
      let result = [...rows];

      // Sort
      result?.sort((a, b) => {
        if (a[sortField] < b[sortField]) return sortOrder === "asc" ? -1 : 1;
        if (a[sortField] > b[sortField]) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });

      return result;
    } else {
      return [];
    }
  }, [sortField, sortOrder, rows]);

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const tableHeadings = {
    brand_id: "Brand ID",
    brand_name: "Brand Name",
    total_conversion_from_an_aggregated_report: "Aggregated Conversions",
    total_conversion_of_players_report: "Player Level Conversions",
    gap: "Gap",
  };

  const headCells = Object.entries(tableHeadings);

  const getCalculatedRows = () => {
    return rows?.reduce((sum: any, item: any) => sum + item.gap, 0);
  };

  return (
    <>
      <Accordion
        expanded={expanded === "panel1"}
        onChange={handleChange("panel1")}
        sx={{ margin: "0 !important" }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1bh-content"
          id="panel1bh-header"
        >
          <Typography
            component="span"
            sx={{
              width: "33%",
              flexShrink: 0,
              fontSize: "24px !important",
              fontWeight: 400,
            }}
          >
            Gaps in Brand Data
            <Typography fontSize={14} fontWeight={400} color="text.secondary">
              Failed due to this reason:{" "}
              {rows?.length > 0 ? getCalculatedRows() : 0}{" "}
              {`(${percentage}% of ${total} FTD’s)`}
            </Typography>
          </Typography>
          <Typography
            component="span"
            sx={{
              color: "text.secondary",
              fontSize: 12,
              width: "50%",
              display: "flex",
              alignItems: "center",
            }}
          >
            See how many conversions were reported at the aggregate level, how
            many at the player level, and the gap between them by brand.
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: "8px 8px 8px 0px !Important" }}>
          {expanded && rows?.length > 0 ? (
            <TableContainer
              className="PageScantablewrap"
              sx={{ maxHeight: 588 }}
            >
              {rows?.length > 0 ? (
                <Table aria-label="collapsible table" size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {headCells.map(([key, value], index) => (
                        <TableCell key={index} align="left">
                          <TableSortLabel
                            active={sortField === key}
                            direction={sortField === key ? sortOrder : "asc"}
                            onClick={() => handleSortClick(key)}
                          >
                            {value}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAndSortedData?.map((row, rowIndex) => (
                      <React.Fragment key={rowIndex}>
                        <TableRow
                          sx={{
                            "& > *": {
                              borderBottom:
                                "1px solid rgba(224, 224, 224, 1) !important",
                            },
                          }}
                        >
                          {headCells.map(([key], cellIndex) => (
                            <TableCell key={cellIndex} align="left">
                              {key === "brand_id" ? (
                                <>{row[key] ? row[key] : "N/A"}</>
                              ) : key === "brand_name" ? (
                                <> {row[key] ? row[key] : "N/A"}</>
                              ) : key ===
                                "total_conversion_from_an_aggregated_report" ? (
                                <> {row[key] ? row[key] : "N/A"}</>
                              ) : key ===
                                "total_conversion_of_players_report" ? (
                                <>{row[key] ? row[key] : "N/A"}</>
                              ) : key === "gap" ? (
                                <>{row[key] ? row[key] : "N/A"}</>
                              ) : row[key] ? (
                                row[key]
                              ) : (
                                "N/A"
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Alert
                  sx={{ color: "#ED6C02", mb: 1 }}
                  severity="warning"
                  icon={<InfoOutlinedIcon fontSize="inherit" sx={{ mr: 1 }} />}
                >
                  NO DATA FOR Gap in Brand Data
                </Alert>
              )}
            </TableContainer>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100px", // gives some vertical space
                width: "100%",
              }}
            >
              <Typography fontSize={20} fontWeight={500} color="text.secondary">
                NO DATA FOR Gap in Brand Data
              </Typography>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default GapsinBrandData;
